import styled from '@emotion/styled';
import {
  formatOffsetInMeterBasedOnUnitType,
  formatTimeDuration,
} from '@racemap/utilities/formatting';
import {
  type PreparedStarterInfo,
  getVirtualRaceDistance,
  getVirtualRaceDuration,
  sortTimes,
  testEventHasVirtDuration,
} from '@racemap/utilities/functions/timing';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { Splits } from '@racemap/utilities/types/types';
import { Progress } from 'antd';
import type { Immutable } from 'immer';
import type { ReactNode } from 'react';
import { Center } from '../../../BasicComponents/MetaComponent';
import type { Column } from '../TableBody';

function ProgressColComponent({
  starterInfo,
  event,
  splits,
}: {
  starterInfo: Immutable<PreparedStarterInfo>;
  event: Immutable<RacemapEvent>;
  splits: Immutable<Splits>;
}) {
  const eventVirtualRaceDistance = getVirtualRaceDistance(event, splits);
  const eventVirtualRaceDuration = getVirtualRaceDuration(event);
  const eventHasVirtDuration = testEventHasVirtDuration(event);
  const unit = event.playerOptions.unitType;
  const now =
    Math.floor(
      (starterInfo?.progress /
        (eventHasVirtDuration ? eventVirtualRaceDuration : eventVirtualRaceDistance)) *
        100000,
    ) / 1000;

  return (
    <Cell>
      <div>
        {eventHasVirtDuration
          ? starterInfo?.progress === -1
            ? '00:00:00'
            : formatTimeDuration(starterInfo?.progress)
          : starterInfo?.progress === -1
            ? '0km'
            : formatOffsetInMeterBasedOnUnitType(starterInfo?.progress, unit)}
      </div>
      <ProgressContainer>
        {now > 0 && (
          <Progress
            percent={now}
            size="small"
            showInfo={false}
            status={now >= 100 ? 'success' : undefined}
          />
        )}
      </ProgressContainer>
    </Cell>
  );
}

export function getProgressCol(
  event: Immutable<RacemapEvent>,
  splits: Immutable<Splits>,
  windowWidth: number,
): Column {
  const eventHasVirtDuration = testEventHasVirtDuration(event);

  return {
    key: 'current_progress',
    title: <HeaderCell>{windowWidth < 660 ? 'Curr. Progress' : 'Current Progress'}</HeaderCell>,
    width: windowWidth < 660 ? 100 : 140,
    render: function renderer(row: Immutable<PreparedStarterInfo>): ReactNode {
      return <ProgressColComponent starterInfo={row} event={event} splits={splits} />;
    },
    sorter: (a: Immutable<PreparedStarterInfo>, b: Immutable<PreparedStarterInfo>) =>
      eventHasVirtDuration ? sortTimes(a?.progress, b?.progress) : a?.progress - b?.progress,
  };
}

const Cell = styled(Center)`
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const ProgressContainer = styled.div`
  width: 90%;
  height: 15px;
`;

const HeaderCell = styled(Center)`
  width: 100%;
  font-weight: 600;
`;
